import { isDefined } from "@clipboard-health/util-ts";
import { type MutableRefObject, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { APP_V2_APP_EVENTS, logEvent } from "../../../lib/analytics";
import { showErrorToast } from "../../../lib/Notifications";
import { completeMagicLinkSignIn } from "../../cognito/cognitoAuth";
import { formatSessionInitErrorMessage } from "../../cognito/formatSessionInitErrorMessage";
import { initCognitoSession, SESSION_INIT_OUTCOME } from "../../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../../cognito/isSafeRedirectUrl";
import { clearMagicLinkEmail, type MagicLinkAuthContext } from "../../cognito/magicLinkEmailStore";
import { mapCognitoError } from "../../cognito/mapCognitoError";
import { requestRememberEmail } from "../../rememberEmail/requestRememberEmail";
import { getStoredLoginFlowTelemetryCohort, LOGIN_FLOW } from "../../loginFlowTelemetry";
import {
  recordTerminalLoginFailure,
  recordTerminalLoginSuccess,
} from "../../loginJourneyTelemetry";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "../../loginRum";
import { clearRedirectInProgress, markRedirectInProgress } from "../../cognito/redirectInProgress";
import { runSessionHydrationWithReloadRecovery } from "../../cognito/sessionHydrationReloadRecovery";
import { type AuthState, type EmailSource } from "../types";

const DEFAULT_REDIRECT_PATH = "/";

interface UseMagicLinkRedeemArguments {
  payload: string | null;
  searchParams: URLSearchParams;
  hasAttemptedAuth: MutableRefObject<boolean>;
  setAuthState: (state: AuthState) => void;
  setError: (error: string | undefined) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux dispatch
  dispatch: any;
}

/**
 * Encapsulates the magic-link redemption flow — Cognito sign-in, Redux
 * session hydration, and final navigation. Returns a stable callback
 * AuthRedirect wires into its button handlers.
 *
 * Extracted from AuthRedirect.tsx to keep that file under the max-lines
 * and max-dependencies limits.
 */
export function useMagicLinkRedeem({
  payload,
  searchParams,
  hasAttemptedAuth,
  setAuthState,
  setError,
  dispatch,
}: UseMagicLinkRedeemArguments): (
  authContext: MagicLinkAuthContext,
  source: EmailSource
) => Promise<void> {
  const history = useHistory();

  return useCallback(
    async (authContext: MagicLinkAuthContext, source: EmailSource) => {
      if (hasAttemptedAuth.current) {
        return;
      }
      hasAttemptedAuth.current = true;
      const emailToUse = authContext.email;

      setAuthState("signing-in");
      setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);

      try {
        const requestedUrl = searchParams.get("requestedUrl");
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_STARTED);

        if (!payload) {
          recordTerminalLoginFailure();
          logAuthRedirectFailure({
            error: new Error("Missing payload in magic link"),
            metadata: {
              errorCode: "auth/invalid-link",
              errorMessage: "Missing payload",
              outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
              reason: "auth/invalid-link",
            },
          });
          setError("This login link is invalid or incomplete. Please request a new login link.");
          setAuthState("error");
          return;
        }

        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_EMAIL_RESOLVED, { source });

        await completeMagicLinkSignIn(
          authContext.authUsername
            ? { payload, email: emailToUse, authUsername: authContext.authUsername }
            : { payload, email: emailToUse }
        );
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_COGNITO_SUCCESS);

        setAuthState("redirecting");

        const target =
          requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

        // Flag the impending redirect BEFORE dispatching the session thunk —
        // the dispatch flips `isAuthorized` mid-await, causing the parent
        // route tree to remount AuthRedirect. The fresh instance reads this
        // flag and stays in the loading state instead of rendering the
        // "already authenticated" consent screen.
        markRedirectInProgress();

        // Hydrate the Redux session before navigating so PrivateRoute sees a
        // valid session immediately, avoiding a flash of the login page.
        const result = await runSessionHydrationWithReloadRecovery({
          enabled: getStoredLoginFlowTelemetryCohort() === LOGIN_FLOW.REDESIGNED,
          hydrate: async () => await dispatch(initCognitoSession({ authMethod: "non-passkey" })),
          source: "magic-link",
        });
        if (!isDefined(result)) {
          return;
        }

        // Surface a hydration failure here rather than navigating with no
        // session — PrivateRoute would bounce the user back to /login with
        // no indication of why.
        if (
          result.outcome !== SESSION_INIT_OUTCOME.HYDRATED &&
          result.outcome !== SESSION_INIT_OUTCOME.WORKER_ACCOUNT
        ) {
          throw result.error ?? new Error("Failed to hydrate session after magic-link sign-in");
        }

        // Request the post-sign-in "remember your email?" prompt only after
        // hydration succeeds. Requested earlier, its focus-trapping dialog can
        // stay stuck over the page when hydration fails (worker account, or a
        // transient/403 error) and PrivateRoute bounces back to /login. On the
        // native app it's remembered silently.
        if (result.outcome === SESSION_INIT_OUTCOME.HYDRATED) {
          requestRememberEmail(emailToUse);
        }

        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_SUCCESS, {
          outcome: LOGIN_OUTCOME.SUCCESS,
        });
        recordTerminalLoginSuccess();
        history.replace(target);
        clearRedirectInProgress();
        clearMagicLinkEmail();
      } catch (authError: unknown) {
        hasAttemptedAuth.current = false;
        clearRedirectInProgress();
        const mappedError = mapCognitoError(authError);
        const { outcome, reason } =
          mappedError.code === "auth/unknown" && isNetworkFailure(authError)
            ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
            : classifyCognitoError(mappedError.code);

        recordTerminalLoginFailure();
        logAuthRedirectFailure({
          error: authError,
          metadata: {
            errorCode: mappedError.code,
            errorMessage: mappedError.message,
            email: emailToUse,
            outcome,
            reason,
          },
        });
        if (mappedError.code === "auth/user-type-not-allowed") {
          logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_USER_TYPE_NOT_ALLOWED, { email: emailToUse });
        }

        // Keep the link-expired context for generic/unknown errors, while the
        // formatter appends unmapped raw error details so screenshots remain
        // useful for debugging. Mapped errors still get their specific copy.
        const errorMessage = formatSessionInitErrorMessage(authError, {
          networkFailureMessage:
            "We couldn't reach Clipboard's servers. Check your internet connection and try again.",
          noAssociatedWorkplacesMessage:
            "Your account is active, but it isn't linked to an active workplace. Ask a workplace administrator to link your account to a workplace, then try again.",
          defaultMessage:
            "Link verification failed. The link may have expired. Please request a new login link.",
        });

        setError(errorMessage);
        setAuthState("error");
        showErrorToast(errorMessage);
      }
    },
    [payload, searchParams, history, dispatch, hasAttemptedAuth, setAuthState, setError]
  );
}
